import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type {
  AssignmentDetail,
  BookingRequestRead,
  ConnectedFacility,
  ExpectedExpensesEnum,
  ProviderBookingRequestWriteDetail,
} from '../../../js/api';
import {
  AssignmentsService,
  BookingRequestsUpdateService,
  ConnectionsService,
} from '../../../js/api';
import { TravelGuidelines } from '../../../js/types/TravelGuidelines';
import { useCodeDefinitionsAsOptions } from '../hooks/useCodeDefinitions';
import {
  ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY,
  ASSIGNMENT_QUERY_KEY,
  ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
} from '../pages/Organization/Assignments/queryKeys';
import {
  CONNECTION_EVENT_HISTORY_QUERY_KEY,
  ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY,
} from '../pages/Organization/Connections/queryKeys';
import { CONNECTION_QUERY_KEY } from '../pages/Provider/Connections/queryKeys';
import { Button } from './button';
import { Checkbox, CheckboxField, CheckboxGroup } from './checkbox';
import { Label } from './fieldset';
import { Textarea } from './textarea';
import { TooltippedText } from './tooltipped-text';

interface ExpenseAndTermsFormProps {
  entity: ConnectedFacility | BookingRequestRead | AssignmentDetail;
  entityType: 'connection' | 'booking' | 'assignment';
  onUpdate: () => void;
  editAdditionalTerms?: boolean;
  sectionLabel?: string;
  readOnly?: boolean;
  className?: string;
  defaultExpanded?: boolean;
}

const ToggleButton = ({
  entityType,
  showForm,
  onClick,
  sectionLabel,
}: {
  entityType: 'connection' | 'booking' | 'assignment';
  showForm: boolean;
  onClick: () => void;
  sectionLabel: string;
}) => {
  const buttonClasses = 'tw:flex tw:items-center tw:text-decoration-none tw:gap-2 tw:font-medium';
  if (entityType === 'booking') {
    return (
      <button type="button" onClick={onClick} className={buttonClasses}>
        <i className={`fas ${showForm ? 'fa-caret-down' : 'fa-caret-right'} tw:mr-2`} />
        {sectionLabel}
      </button>
    );
  }

  return (
    <Button
      color="link"
      onClick={onClick}
      className={buttonClasses}
      data-testid="expense-terms-toggle"
    >
      <i className={`fas ${showForm ? 'fa-caret-down' : 'fa-caret-right'}`} />
      <span>{sectionLabel}</span>
    </Button>
  );
};

export function ExpenseAndTermsForm({
  entity,
  entityType,
  onUpdate,
  editAdditionalTerms = true,
  sectionLabel = 'Expenses & Terms',
  readOnly = false,
  className,
  defaultExpanded = false,
}: ExpenseAndTermsFormProps) {
  const [showForm, setShowForm] = useState(defaultExpanded);
  const [expectedExpenses, setExpectedExpenses] = useState<ExpectedExpensesEnum[]>(
    entity.expected_expenses || []
  );
  const [additionalTerms, setAdditionalTerms] = useState(entity.additional_terms || '');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (entityType === 'connection') {
        return ConnectionsService.connectionsConnectedFacilityPartialUpdate({
          id: entity.id,
          requestBody: {
            expected_expenses: expectedExpenses,
            additional_terms: additionalTerms,
          },
        });
      } else if (entityType === 'assignment') {
        return AssignmentsService.assignmentsAssignmentPartialUpdate({
          id: entity.id,
          requestBody: {
            expected_expenses: expectedExpenses,
            additional_terms: additionalTerms,
          },
        });
      } else {
        return BookingRequestsUpdateService.bookingRequestsUpdatePartialUpdate({
          requestId: entity.id,
          requestBody: {
            expected_expenses: expectedExpenses,
            additional_terms: additionalTerms,
          } as ProviderBookingRequestWriteDetail,
        });
      }
    },
    onSuccess: async () => {
      setSaveSuccess(true);
      setSaveError(null);
      if (entityType === 'connection') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, entity.id] }),
          queryClient.invalidateQueries({
            queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, entity.id],
          }),
        ]);
      } else if (entityType === 'assignment') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, entity.id] }),
          queryClient.invalidateQueries({
            queryKey: [ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY, entity.id],
          }),
        ]);
      } else {
        await queryClient.invalidateQueries({ queryKey: ['booking-requests'] });
      }
      onUpdate();
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error: unknown) => {
      setSaveError(error instanceof Error ? error.message : 'Failed to update');
      setSaveSuccess(false);
    },
  });

  const handleExpenseChange = (expense: ExpectedExpensesEnum) => {
    setExpectedExpenses((prev) =>
      prev.includes(expense) ? prev.filter((e) => e !== expense) : [...prev, expense]
    );
  };

  const handleSave = () => {
    updateMutation.mutate();
  };

  const { data: expenseOptions = [] } =
    useCodeDefinitionsAsOptions<ExpectedExpensesEnum>('ExpenseCategory');

  const travelGuidelines: TravelGuidelines | undefined =
    entityType === 'connection' || entityType === 'assignment'
      ? (entity as ConnectedFacility | AssignmentDetail)?.healthcare_entity?.metadata
          ?.travel_guidelines
      : {};

  const travelCaps = travelGuidelines?.travel_caps?.length ? (
    <div className="tw:text-xs tw:leading-snug tw:max-w-xs tw:mt-1">
      {travelGuidelines?.effective_date && (
        <div className="tw:text-[11px] tw:opacity-80 tw:mb-1 tw:ml-2">
          Effective: {travelGuidelines.effective_date}
        </div>
      )}
      <ul className="tw:!ml-2 tw:!pl-0 tw:space-y-2">
        {travelGuidelines.travel_caps.map((cap) => (
          <li key={cap.expense_kind}>
            <div className="tw:font-semibold tw:mb-0.5">{cap.expense_kind}</div>
            <ul className="tw:list-disc tw:ml-1 tw:space-y-0.5">
              {cap.caps.map((row, idx) => (
                <li key={`${cap.expense_kind}-${idx}`}>
                  {row.allowed_amount} ({row.type})
                </li>
              ))}
            </ul>
            {cap.disclaimers && (
              <div className="tw:mt-1 tw:text-[10px] tw:whitespace-pre-wrap tw:ml-5">
                Disclaimer: {cap.disclaimers}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <div className="tw:text-xs">No travel caps available</div>
  );

  return (
    <div className={`tw:mt-3 ${className || ''}`}>
      <ToggleButton
        entityType={entityType}
        showForm={showForm}
        onClick={() => setShowForm(!showForm)}
        sectionLabel={sectionLabel}
      />

      {showForm && (
        <div className="tw:px-3 tw:pb-3">
          {readOnly ? (
            <>
              {expectedExpenses && expectedExpenses.length > 0 && (
                <div className="tw:ml-3 tw:mb-4 tw:pb-4 tw:border-b tw:border-zinc-200">
                  <div className="tw:italic tw:text-zinc-600 dark:tw:text-zinc-300 tw:mb-2">
                    Reimbursable expenses for assignment
                    {travelGuidelines && (
                      <TooltippedText tooltipContent={travelCaps}>
                        <span className="tw:ml-3 tw:text-brand-green-1 tw:cursor-pointer tw:text-sm tw:font-medium tw:hover:underline">
                          <i className="far fa-info-circle tw:mr-1" />
                          Travel Caps
                        </span>
                      </TooltippedText>
                    )}
                  </div>
                  <div className="tw:d-flex tw:flex-wrap tw:gap-2">
                    {expectedExpenses.map((expense) => {
                      const expenseLabel =
                        expenseOptions.find((opt) => opt.value === expense)?.label || expense;
                      return (
                        <span
                          key={expense}
                          className="tw:bg-brand-green-3 tw:border tw:border-brand-green-0/20 tw:text-brand-green-0 tw:px-2 tw:py-1 tw:rounded-full"
                        >
                          {expenseLabel}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {additionalTerms && (
                <div className="tw:ml-3 tw:mb-4 tw:pb-3">
                  <div className="tw:font-medium tw:text-zinc-700 dark:tw:text-zinc-300 tw:mb-2">
                    Additional Terms
                  </div>
                  <div className="tw:text-zinc-600 dark:tw:text-zinc-400 tw:whitespace-pre-wrap">
                    {additionalTerms}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {!editAdditionalTerms && additionalTerms && (
                <div className="tw:mb-4 tw:pb-3 tw:border-bottom">
                  <div className="tw:font-medium tw:text-zinc-700 dark:tw:text-zinc-300 tw:mb-2">
                    Additional Terms
                  </div>
                  <div className="tw:text-zinc-600 dark:tw:text-zinc-400 tw:whitespace-pre-wrap">
                    {additionalTerms}
                  </div>
                </div>
              )}

              <div className="tw:space-y-4">
                <div>
                  <div className="tw:italic tw:text-zinc-600 dark:tw:text-zinc-300 tw:mb-2 tw:leading-tight">
                    Select any anticipated expenses for assignments at this location
                    {travelGuidelines && (
                      <TooltippedText tooltipContent={travelCaps}>
                        <span className="tw:ml-3 tw:text-brand-green-1 tw:cursor-pointer tw:text-sm tw:font-medium tw:hover:underline">
                          <i className="far fa-info-circle tw:mr-1" />
                          Travel Caps
                        </span>
                      </TooltippedText>
                    )}
                  </div>
                  <CheckboxGroup>
                    {expenseOptions.map((option) => (
                      <CheckboxField key={option.value} className="tw:mb-1">
                        <Checkbox
                          color="brandgreen1"
                          checked={expectedExpenses.includes(option.value)}
                          onChange={() => handleExpenseChange(option.value)}
                        />
                        <Label>{option.label}</Label>
                      </CheckboxField>
                    ))}
                  </CheckboxGroup>
                </div>

                {editAdditionalTerms && (
                  <div>
                    <Textarea
                      value={additionalTerms}
                      onChange={(e) => setAdditionalTerms(e.target.value)}
                      placeholder="Enter any additional terms for the confirmation letter..."
                      rows={4}
                    />
                  </div>
                )}

                {saveSuccess && (
                  <div className="tw:py-2 tw:px-3 tw:bg-green-50 tw:border tw:border-green-200 tw:rounded-lg tw:text-green-800 dark:tw:bg-green-900/20 dark:tw:border-green-800 dark:tw:text-green-200">
                    <i className="fas fa-check-circle tw:mr-2" />
                    Successfully updated!
                  </div>
                )}

                {saveError && (
                  <div className="tw:py-2 tw:px-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-red-800 dark:tw:bg-red-900/20 dark:tw:border-red-800 dark:tw:text-red-200">
                    <i className="fas fa-exclamation-circle tw:mr-2" />
                    {saveError}
                  </div>
                )}

                <div className="tw:flex tw:gap-2">
                  <Button
                    color="brandgreen0"
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    data-testid="expense-terms-save"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button outline onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
